import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import type { AxiosError } from 'axios';

import { CACHE_KEY_PHARMAKON_ACCOUNT } from 'src/app/hooks/data/_keys';
import { useBffClient } from 'src/app/hooks/data/useBffClient';
import type { ReactQueryOptions } from 'src/app/types';
import type { BffAccount } from 'src/app/types/account';

export const useAccount = (
  accountId: number,
  options?: Partial<ReactQueryOptions>
): UseQueryResult<BffAccount, AxiosError> => {
  const authClient = useBffClient();

  return useQuery(
    [CACHE_KEY_PHARMAKON_ACCOUNT, accountId],
    () => authClient.get(`/accounts/${accountId}`).then((resp: { data: BffAccount }) => resp.data),
    {
      enabled: options?.enabled && Boolean(accountId) && accountId > 0,
      ...(options?.refetchOnWindowFocus !== undefined && { refetchOnWindowFocus: options?.refetchOnWindowFocus }),
    }
  );
};

interface AccountUpdateParams {
  stripeDefaultCardId: string | null;
}

export const useAccountUpdate = (
  id: number
): UseMutationResult<BffAccount, AxiosError, Partial<AccountUpdateParams>> => {
  const authClient = useBffClient();
  const queryClient = useQueryClient();

  return useMutation(
    ({ stripeDefaultCardId }) =>
      authClient
        .patch(
          `accounts/${id}`,
          {
            ...(stripeDefaultCardId !== undefined && { stripeDefaultCardId }),
          },

          { headers: { 'Content-Type': 'application/json' } }
        )
        .then((resp: { data: BffAccount }) => resp.data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([CACHE_KEY_PHARMAKON_ACCOUNT, id]);
      },
    }
  );
};
